import { useEffect, useRef, useState } from 'react';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { BoxIcon } from '@givebutter/primitives/box-icon';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faEnvelope, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createEngageMessageStripeSession, createMailingsOrder } from 'api/engage';
import { LetterDownloadPreviewButton } from 'assets/js/pages/engage/letters/LetterDownloadPreviewButton';
import { LetterDraftFormValues } from 'assets/js/pages/engage/letters/types';
import { useCurrentLetter } from 'assets/js/pages/engage/letters/useCurrentLetter';
import { useLetterMeta } from 'assets/js/pages/engage/letters/useLetterMeta';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import * as StripeCheckoutDialog from 'components/stripe-checkout-dialog';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useForceRender } from 'customHooks/useForceRender';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { twJoin } from 'tailwind-merge';
import { GivebutterAxiosError } from 'types/error';

interface LetterConfirmMailingDialogProps extends Dialog.DialogProps {}

const LetterConfirmMailingDialog = (props: LetterConfirmMailingDialogProps) => {
    const { children, ...rest } = props;
    const { error, success } = useToast();
    const { key, forceRender } = useForceRender();
    const { accountId } = useCurrentAccount();
    const { data: letter } = useCurrentLetter();
    const { isBodyEmpty, noRecipientCount } = useLetterMeta();
    const [isPaymentCompleted, setIsPaymentCompleted] = useState(false);
    const [isCheckoutDialogOpen, setIsCheckoutDialogOpen] = useState(false);
    const {
        control,
        path,
        watch,
        formState: { isDirty },
    } = Form.useGivebutterFormContext<LetterDraftFormValues>();
    const [hasReviewedMailings] = watch(['hasReviewedMailings']);
    const successRef = useRef<Function | undefined>();

    const { data: paymentData, isFetching: isFetchingPaymentData } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageMessageStripeSession(accountId, letter.id),
        queryFn: () =>
            conditionallyFetchOrNull(
                () => createEngageMessageStripeSession(accountId, letter.id),
                !isBodyEmpty && !noRecipientCount && !isPaymentCompleted,
            ),
        refetchOnWindowFocus: false,
        staleTime: Infinity,
    });

    const { mutate: createMailing } = useMutation({
        mutationFn: () =>
            createMailingsOrder(accountId, letter.id, {
                session_id: paymentData?.session_id ?? '',
            }),
        onSuccess: () => {
            successRef.current = success(
                <div className={'flex items-start gap-1.5 text-sm'}>
                    <p>Your letter has been processed successfully!</p>
                    <Button
                        variant="none"
                        color="neutral"
                        className="font-medium underline"
                        onClick={onCreateLetter}
                    >
                        View details
                    </Button>
                </div>,
                { duration: Infinity, className: '[&>div]:max-w-fit' },
            );
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.message);
        },
    });

    const purchaseButtonIsLoading = isFetchingPaymentData || isDirty;

    const onCreateLetter = () => {
        queryClient.removeQueries({
            queryKey: accountQueryKeys.engageMessageStripeSession(accountId, letter.id),
        });
        queryClient.invalidateQueries({
            queryKey: accountQueryKeys.engageMessageById(accountId, letter.id),
            type: 'all',
            predicate: query => !query.queryKey.includes('stripeSession'),
        });
        forceRender();
    };

    useEffect(() => {
        return () => successRef.current?.();
    }, []);

    return (
        <Dialog.Root key={key} {...rest}>
            {children}
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content
                    className={twJoin(
                        isCheckoutDialogOpen && 'invisible',
                        !isCheckoutDialogOpen && 'visible',
                        'max-w-[25.5rem]',
                    )}
                >
                    <Dialog.Close />
                    <div className="flex flex-col gap-4">
                        <BoxIcon size="lg">
                            <FontAwesomeIcon size="xl" icon={faEnvelope} />
                        </BoxIcon>
                        <div className="text-lg font-semibold">Have you reviewed your mailing?</div>
                        <div className="flex gap-3 text-gray-700">
                            <Form.UseFormCheckboxInput
                                name={path('hasReviewedMailings')}
                                control={control}
                                className="mt-1"
                            />
                            <div className="flex flex-col gap-4">
                                <Form.Label name={path('hasReviewedMailings')}>
                                    I've reviewed my mailings and confirm that they are ready for
                                    sending.
                                </Form.Label>
                                <div className="text-xs text-gray-600">
                                    By placing your order you confirm all text, spelling, and design
                                    elements are correct. You acknowledge that printed colors may
                                    vary slightly from screen display and that refunds will not be
                                    provided for undelivered or lost mail. You understand that no
                                    modifications can be made once the order is placed.
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <LetterDownloadPreviewButton
                                size="md"
                                className="w-full !font-semibold"
                            />
                            <StripeCheckoutDialog.Root
                                open={isCheckoutDialogOpen}
                                clientSecret={paymentData?.client_secret ?? ''}
                                onComplete={() => {
                                    setIsPaymentCompleted(true);
                                    createMailing();
                                }}
                                onOpenChange={open => {
                                    setIsCheckoutDialogOpen(open);
                                    if (!open && isPaymentCompleted) {
                                        onCreateLetter();
                                    }
                                }}
                            >
                                <StripeCheckoutDialog.Trigger asChild>
                                    <Button
                                        variant="solid"
                                        disabled={!hasReviewedMailings || purchaseButtonIsLoading}
                                    >
                                        {purchaseButtonIsLoading && (
                                            <FontAwesomeIcon
                                                icon={faSpinnerThird}
                                                className="animate-spin"
                                            />
                                        )}
                                        Continue to payment
                                    </Button>
                                </StripeCheckoutDialog.Trigger>
                            </StripeCheckoutDialog.Root>
                        </div>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const LetterConfirmMailingDialogTrigger = Dialog.Trigger;
const Root = LetterConfirmMailingDialog;
const Trigger = LetterConfirmMailingDialogTrigger;

export { LetterConfirmMailingDialog, LetterConfirmMailingDialogTrigger, Root, Trigger };
